import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import axios from "../../services/axios";
import { convertLanguageToField } from "../../utils/functions";
import useAuth from "../../hooks/useAuth";
import NoChallengesModal from "../../components/modal/noChallenges";

import Header, {
  NoHeaderScroll,
  BackHeaderLeft,
} from "../../components/navigation/header";
import KamaGameCard from "../../components/kamasutra/gameCard";
import KamaCardSuit from "../../components/kamasutra/cardSuit";

import styles from "./kamaGame.module.css";

function Suit(suit) {
  return (
    <div className="flex items-center justify-center h-full w-full">
      <KamaCardSuit className="w-[50%] max-h-[60%]" suit={suit} />
    </div>
  );
}

function HowTo({ title, desafio }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center p-3 h-full">
      <h1 className="text-3xl font-extrabold uppercase">{title}</h1>
      <span className="text-sm mb-4">{t("step")}</span>
      <p className="font-medium text-xl leading-6 flex-1 scroll">{desafio}</p>
    </div>
  );
}

const timmings = {
  slideUp: 1000,
  slideUpDelay: 300,
  cardSlideUp: 300,
  cardFloat: 2000,
  cardFloatDelay: 120,
  exit: 600,
};

// Card State:
// 0 - inicial
// 1 - slide up -> float
// 2 - selecionado
// 3 - sair pela esquerda
// 4 - sair pela direita

export default function KamasutraGame() {
  const { t, i18n } = useTranslation();
  const { theme } = useLocation().state;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [show, setShow] = useState(true);
  const [leftState, setLeftState] = useState(0);
  const [middleState, setMiddleState] = useState(0);
  const [rightState, setRightState] = useState(0);
  const [fliped, setFliped] = useState(false);
  const [footer, setFooter] = useState(false);
  const [howTo, setHowTo] = useState(false);
  const [playedCards, setPlayedCards] = useState([]);
  const [card, setCard] = useState({});
  const [endedModal, setEndedModal] = useState(false);
  const firstRender = useRef(true);
  const canSelectCard = useRef(false);

  useEffect(() => {
    if (firstRender.current) {
      getCard({});
      firstRender.current = false;
    }
  }, []);

  useEffect(() => {
    setShow(true);
    canSelectCard.current = false;
    setTimeout(() => {
      setLeftState(1);
      setTimeout(() => {
        setMiddleState(1);
        setTimeout(() => {
          setRightState(1);
          canSelectCard.current = true;
        }, timmings.cardSlideUp);
      }, timmings.cardSlideUp);
    }, timmings.slideUp + timmings.slideUpDelay);
  }, [card]);

  useEffect(() => {
    if (fliped) {
      setTimeout(() => {
        setFooter(true);
        setHowTo(true);
      }, 500);
    }
  }, [fliped]);

  const getCard = ({ restart = false }) => {
    if (restart) {
      setPlayedCards([]);
    }
    axios
      .post("/kamasutra/game", {
        category: theme.id_cat_kama,
        exclude: restart ? [] : playedCards,
        id_usuario: user?.id_usuario,
      })
      .then((response) => {
        if (response?.data?.id_kama) {
          const name = convertLanguageToField(i18n.language, "title");
          const description = convertLanguageToField(i18n.language, "desafio");

          setCard({
            ...response.data,
            title: response.data[name],
            desafio: response.data[description],
          });
          setPlayedCards([...playedCards, response.data.id_kama]);
        } else {
          setEndedModal(true);
        }
      });
  };

  const handleClickLeft = () => {
    if (!canSelectCard.current) {
      return;
    }
    if (leftState === 2) {
      setFliped(!fliped);
    } else {
      setLeftState(2);
      setMiddleState(4);
      setRightState(4);
    }
  };
  const handleClickMiddle = () => {
    if (!canSelectCard.current) {
      return;
    }
    if (middleState === 2) {
      setFliped(!fliped);
    } else {
      setMiddleState(2);
      setLeftState(3);
      setRightState(4);
    }
  };
  const handleClickRight = () => {
    if (!canSelectCard.current) {
      return;
    }
    if (rightState === 2) {
      setFliped(!fliped);
    } else {
      setRightState(2);
      setLeftState(3);
      setMiddleState(3);
    }
  };

  const rightCardTimmings = () => {
    switch (rightState) {
      case 2:
      case 3:
      case 4:
        return { "--animation-duration": `${timmings.exit}ms` };
      default:
        return {
          "--animation-duration": `${timmings.cardFloat}ms`,
          "--animation-delay": `${
            timmings.slideUp +
            timmings.slideUpDelay +
            timmings.cardSlideUp +
            timmings.cardFloatDelay * 2
          }ms`,
        };
    }
  };

  const middleCardTimmings = () => {
    switch (middleState) {
      case 2:
      case 3:
      case 4:
        return { "--animation-duration": `${timmings.exit}ms` };
      default:
        return {
          "--animation-duration": `${timmings.cardFloat}ms`,
          "--animation-delay": `${
            timmings.slideUp +
            timmings.slideUpDelay +
            timmings.cardSlideUp +
            timmings.cardFloatDelay
          }ms`,
        };
    }
  };

  const leftCardTimmings = () => {
    switch (leftState) {
      case 2:
      case 3:
      case 4:
        return { "--animation-duration": `${timmings.exit}ms` };
      default:
        return {
          "--animation-duration": `${timmings.cardFloat}ms`,
          "--animation-delay": `${
            timmings.slideUp + timmings.slideUpDelay + timmings.cardSlideUp * 2
          }ms`,
        };
    }
  };

  const handleHowToDo = () => {
    setFliped(!fliped);
  };
  const handleNext = (props = {}) => {
    setShow(false);
    setFooter(false);
    setTimeout(() => {
      setLeftState(0);
      setMiddleState(0);
      setRightState(0);
      setFliped(false);
      setHowTo(false);
      getCard(props);
    }, 1000);
  };

  const handleGoBack = () => {
    setEndedModal(false);
    navigate("/kamasutra/themes");
  };
  const handleRestart = () => {
    setPlayedCards([]);
    setEndedModal(false);
    handleNext({ restart: true });
  };

  return (
    <div className="container no-scroll mx-auto">
      <Header HeaderLeft={BackHeaderLeft} HeaderCenter={() => {}} />
      <NoHeaderScroll className="flex flex-col ps-5 pe-5">
        <div
          className={`${styles.container} ${
            show ? styles["show"] : styles["hide"]
          }`}
          style={{ "--animation-duration": `${timmings.slideUp}ms` }}
        >
          <KamaGameCard
            className={`${styles.card} ${styles.right}
            ${rightState === 1 ? styles["slide-up"] : ""}
            ${rightState === 2 ? styles["selected"] : ""}
            ${rightState === 3 ? styles["exit-left"] : ""}
            ${rightState === 4 ? styles["exit-right"] : ""}
            ${fliped ? styles["fliped"] : ""}
            `}
            borderSize={25}
            style={rightCardTimmings()}
            onClick={handleClickRight}
            Background={() => Suit("hearts-gray")}
            Back={() =>
              howTo && rightState === 2 ? HowTo(card) : Suit("hearts")
            }
            hotLevel={rightState === 2 && card.nivel}
            image={rightState === 2 && card.img_carta}
            number={rightState === 2 && card.id_kama}
          />
          <KamaGameCard
            className={`${styles.card} ${styles.middle} 
            ${middleState === 1 ? styles["slide-up"] : ""}
            ${middleState === 2 ? styles["selected"] : ""}
            ${middleState === 3 ? styles["exit-left"] : ""}
            ${middleState === 4 ? styles["exit-right"] : ""}
            ${fliped ? styles["fliped"] : ""}
            `}
            borderSize={25}
            style={middleCardTimmings()}
            onClick={handleClickMiddle}
            Background={() => Suit("spades-gray")}
            Back={() =>
              howTo && middleState === 2 ? HowTo(card) : Suit("spades")
            }
            hotLevel={middleState === 2 && card.nivel}
            image={middleState === 2 && card.img_carta}
            number={middleState === 2 && card.id_kama}
          />
          <KamaGameCard
            className={`${styles.card} ${styles.left} 
            ${leftState === 1 ? styles["slide-up"] : ""}
            ${leftState === 2 ? styles["selected"] : ""}
            ${leftState === 3 ? styles["exit-left"] : ""}
            ${leftState === 4 ? styles["exit-right"] : ""}
            ${fliped ? styles["fliped"] : ""}
            `}
            borderSize={25}
            style={leftCardTimmings()}
            onClick={handleClickLeft}
            Background={() => Suit("clubs-gray")}
            Back={() =>
              howTo && leftState === 2 ? HowTo(card) : Suit("clubs")
            }
            hotLevel={leftState === 2 && card.nivel}
            image={leftState === 2 && card.img_carta}
            number={leftState === 2 && card.id_kama}
          />
        </div>
        <div
          className={`${styles.footer} ${
            footer ? styles["slide-up"] : !show ? styles["slide-down"] : ""
          }`}
        >
          <button
            type="button"
            onClick={handleHowToDo}
            className="btn btn-outline-brand p-1 text-lg rounded-lg mb-2"
          >
            {t("howToDo")}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="btn btn-brand p-2 text-xl rounded-lg"
          >
            {t("next")}
          </button>
        </div>
      </NoHeaderScroll>
      <NoChallengesModal
        title={t("noChallenges")}
        text1={t("dialogText1HotChallenges")}
        text2={t("dialogText2HotChallenges")}
        visible={endedModal}
        handleGoBack={handleGoBack}
        handleRestart={handleRestart}
      />
    </div>
  );
}
